import { Link } from 'react-router-dom'
import { SEO, FAQSchema } from '@/components/seo'
import { ArrowRight, FileX, Check, AlertTriangle } from 'lucide-react'
import { Dropzone } from '@/components/dropzone/Dropzone'
import { buttonStyles } from '@/components/ui'
import { Stats, Testimonials, BeforeAfter } from '@/components/sections'

export function MetadataCleaner() {
  const faqItems = [
    {
      question: 'What metadata does this remove?',
      answer: 'We remove EXIF data (camera info, timestamps, GPS), XMP data (editing history), IPTC data (descriptions), C2PA Content Credentials, AI generation parameters, and all other embedded metadata.',
    },
    {
      question: 'Does removing metadata affect image quality?',
      answer: 'No, metadata is stored separately from the image pixels. Removing it does not change how the image looks.',
    },
    {
      question: 'Will this remove GPS location data?',
      answer: 'Yes, all location/GPS data embedded in EXIF is completely removed for your privacy.',
    },
    {
      question: 'Is this different from watermark removal?',
      answer: 'Yes, metadata removal handles data stored in the file. Watermarks like SynthID are embedded in the actual image pixels and require different processing.',
    },
  ]

  return (
    <>
      <SEO
        title="Image Metadata Cleaner - Remove EXIF, C2PA, AI Tags Free"
        description="Free image metadata cleaner. Remove EXIF, C2PA Content Credentials, AI generation tags, GPS data. Strip all metadata from images instantly."
        canonical="/tools/metadata-cleaner"
        keywords="metadata cleaner, remove exif, strip metadata, c2pa remover, ai metadata remover"
      />
      <FAQSchema questions={faqItems} />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-red-50 via-white to-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full text-sm text-red-700 font-medium mb-6">
              <FileX className="w-4 h-4" />
              Metadata Removal Tool
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Clean All{' '}
              <span className="text-red-600">Image Metadata</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Remove EXIF, C2PA, XMP, IPTC, and all embedded metadata from your images.
              Protect your privacy and remove AI generation markers.
            </p>
          </div>

          {/* Dropzone */}
          <div className="max-w-2xl mx-auto">
            <Dropzone onFileSelect={() => {}} />
          </div>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              EXIF removal
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              C2PA stripping
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              GPS data removed
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Trusted By */}
      <Stats />

      {/* Before/After Examples */}
      <BeforeAfter />

      {/* Testimonials */}
      <Testimonials />

      {/* What We Remove */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Metadata We Remove
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: 'EXIF Data',
                items: ['Camera make/model', 'Timestamps', 'GPS coordinates', 'Exposure settings', 'Software used'],
                icon: '📷',
              },
              {
                title: 'C2PA / Content Credentials',
                items: ['AI generation markers', 'Tool identification', 'Creation history', 'Digital signatures'],
                icon: '📋',
              },
              {
                title: 'XMP Data',
                items: ['Editing history', 'Adobe tags', 'Rating/labels', 'Creator info'],
                icon: '🏷️',
              },
              {
                title: 'IPTC Data',
                items: ['Descriptions', 'Keywords', 'Copyright info', 'Contact details'],
                icon: '📝',
              },
              {
                title: 'AI Generation Tags',
                items: ['Prompts', 'Model names', 'Generation parameters', 'Seed values'],
                icon: '🤖',
              },
              {
                title: 'Other Metadata',
                items: ['Thumbnails', 'ICC profiles (optional)', 'Custom namespaces', 'Comments'],
                icon: '🗂️',
              },
            ].map((category, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <span className="text-2xl mb-3 block">{category.icon}</span>
                <h3 className="font-semibold text-gray-900 mb-3">{category.title}</h3>
                <ul className="space-y-1">
                  {category.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy Warning */}
      <section className="section bg-yellow-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Why Metadata Privacy Matters
                </h2>
                <p className="text-gray-700 mb-4">
                  Images often contain more information than you realize:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li><strong>Location tracking</strong> - GPS coordinates reveal where photos were taken</li>
                  <li><strong>Device fingerprinting</strong> - Camera serial numbers can identify your device</li>
                  <li><strong>AI disclosure</strong> - C2PA metadata automatically labels images as AI-generated</li>
                  <li><strong>Editing history</strong> - XMP data shows what software you used</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section bg-white">
        <div className="container">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Common Use Cases
          </h2>

          <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { title: 'Privacy Protection', description: 'Remove GPS and personal data before sharing online' },
              { title: 'AI Image Cleaning', description: 'Strip C2PA and AI tags to avoid platform labels' },
              { title: 'Professional Delivery', description: 'Clean files for client handoff' },
              { title: 'Stock Submission', description: 'Prepare images for stock photo sites' },
            ].map((item, index) => (
              <div key={index} className="text-center p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <details key={index} className="group bg-white rounded-xl">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="font-medium text-gray-900">{item.question}</span>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-gray-600">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-red-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Clean Your Image Metadata Now
          </h2>
          <p className="text-red-100 mb-8 max-w-xl mx-auto">
            Free, instant, and completely private. Your images never leave your browser.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-red-600 hover:bg-gray-100',
            })}
          >
            Start Free
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  )
}
